import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Advertisement from './Advertisement';
import './MainScreen.css';

const MainScreen = ({ onStartGame, user }) => {
  const { t } = useTranslation();
  const [difficulty, setDifficulty] = useState(null);
  const [gamemode, setGamemode] = useState(null);
  const [gamemodes, setGamemodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  // State for showing game results (only using the value, not the setter)
  const [showGameResults] = useState(false);


  // Fetch gamemodes and categories from the backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch gamemodes
        const gamemodesResponse = await fetch('/api/gamemodes');
        if (!gamemodesResponse.ok) {
          throw new Error('Failed to fetch gamemodes');
        }
        const gamemodesData = await gamemodesResponse.json();
        setGamemodes(gamemodesData);

        // Fetch categories
        const categoriesResponse = await fetch('/api/gamemode-categories');
        if (!categoriesResponse.ok) {
          throw new Error('Failed to fetch categories');
        }
        const categoriesData = await categoriesResponse.json();
        setCategories(categoriesData);

        // Set default active category to the first one
        if (categoriesData.length > 0) {
          setActiveCategory(categoriesData[0]);
        }

        // Set default gamemode to the first one in the active category
        if (gamemodesData.length > 0) {
          const firstGamemodeInCategory = gamemodesData.find(
            mode => mode.category === (categoriesData.length > 0 ? categoriesData[0] : null)
          );

          let selectedGamemode;
          if (firstGamemodeInCategory) {
            setGamemode(firstGamemodeInCategory.id);
            selectedGamemode = firstGamemodeInCategory;
          } else {
            setGamemode(gamemodesData[0].id);
            selectedGamemode = gamemodesData[0];
          }

          // Set initial difficulty based on the selected gamemode
          if (selectedGamemode && selectedGamemode.difficulty) {
            setDifficulty(selectedGamemode.difficulty);
          } else {
            // Default to 'medium' if no difficulty is specified
            setDifficulty('medium');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleStartGame = () => {
    // If gamemodes are available, use gamemode, otherwise fall back to difficulty
    if (gamemodes.length > 0 && gamemode) {
      onStartGame(difficulty, gamemode);
    } else {
      onStartGame(difficulty);
    }
  };


  const handleGamemodeChange = (e) => {
    const selectedGamemodeId = parseInt(e.target.value);
    setGamemode(selectedGamemodeId);

    // Find the selected gamemode object
    const selectedGamemode = gamemodes.find(mode => mode.id === selectedGamemodeId);
    if (selectedGamemode && selectedGamemode.difficulty) {
      // Update difficulty based on the selected gamemode
      setDifficulty(selectedGamemode.difficulty);
    } else {
      // Default to 'medium' if no difficulty is specified
      setDifficulty('medium');
    }
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);

    // Find the first gamemode in this category and select it
    const firstGamemodeInCategory = gamemodes.find(mode => mode.category === category);
    if (firstGamemodeInCategory) {
      setGamemode(firstGamemodeInCategory.id);

      // Update difficulty based on the selected gamemode
      if (firstGamemodeInCategory.difficulty) {
        setDifficulty(firstGamemodeInCategory.difficulty);
      } else {
        // Default to 'medium' if no difficulty is specified
        setDifficulty('medium');
      }
    }
  };

  return (
    <div id="main-screen" className="main-screen">
      {user && user.streamer_mode && (
        <div className="streamer-mode-bar">
          <span>{t('userConfig.streamerMode')}</span>
        </div>
      )}
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="logo-section">
            <img src="/images/logo.png" alt="Logo de OverGuessr" />
          </div>

          <div className="game-options">
            {showGameResults && (
              <div id="game-results" className="game-results">
                <h2>{t('game.previousResults')}</h2>
                <div id="main-screen-results"></div>
              </div>
            )}

            {!user && (
              <div id="login-reminder" className="login-reminder">
                <p>{t('auth.gameLoginReminder')}</p>
                <a href="/auth/battlenet" className="btn btn-secondary">
                  {t('auth.loginButton')}
                </a>
              </div>
            )}

            <div className="difficulty-selector">
              <p>{t('game.description')}</p>

              {loading ? (
                <div className="loading">{t('game.loadingGameModes')}</div>
              ) :  (
                <div className="gamemode-options">
                  <h3>{t('game.selectGameMode')}</h3>

                  <div className="category-tabs">
                    <div className="tabs-container">
                      <div className="tabs-left">
                        {categories.map(category => (
                          <div 
                            key={category} 
                            className={`tab ${activeCategory === category ? 'active' : ''}`}
                            onClick={() => handleCategoryChange(category)}
                          >
                            {t(`gamemode.${category.toLowerCase()}.${category.toLowerCase()}`, category.toLowerCase())}
                          </div>
                        ))}
                      </div>

                      <div className="tabs-content">
                        <div className="radio-group">
                          {gamemodes
                            .filter(mode => mode.category === activeCategory)
                            .map(mode => (
                              <label key={mode.id} className={`radio-container ${gamemode === mode.id ? 'selected' : ''}`}>
                                <input 
                                  type="radio" 
                                  name="gamemode" 
                                  value={mode.id}
                                  data-difficulty={mode.difficulty}
                                  checked={gamemode === mode.id} 
                                  onChange={handleGamemodeChange} 
                                />
                                <span className="radio-label">{t(`gamemode.${mode.category.toLowerCase()}.${mode.code.toLowerCase()}`, mode.name.toLowerCase())}</span>
                                <span className="radio-description">{t(`gamemode.${mode.category.toLowerCase()}.${mode.code.toLowerCase()}-description`, mode.code ? mode.code.toLowerCase() : '')}</span>
                              </label>
                            ))
                          }
                        </div>
                      </div>
                    </div>
                    <Advertisement user={user} adType="mainScreen" className="ad-container" />
                  </div>
                </div>
              )}

              <button 
                id="start-game-btn" 
                className="btn btn-primary start-game-btn" 
                onClick={handleStartGame}
              >
                <i className="fas fa-play"></i> {t('game.startButton')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="main-content">
        <div className="container">
          <div className="how-to-play">
            <h2>{t('howToPlay.title')}</h2>
            <div className="steps">
              <div className="step">
                <div className="step-icon">
                  <i className="fas fa-eye"></i>
                </div>
                <h3>{t('howToPlay.step1Title')}</h3>
                <p>{t('howToPlay.step1Description')}</p>
              </div>

              <div className="step">
                <div className="step-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <h3>{t('howToPlay.step2Title')}</h3>
                <p>{t('howToPlay.step2Description')}</p>
              </div>

              <div className="step">
                <div className="step-icon">
                  <i className="fas fa-trophy"></i>
                </div>
                <h3>{t('howToPlay.step3Title')}</h3>
                <p>{t('howToPlay.step3Description')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainScreen;
